import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, CircleNotch, WarningCircle, LockKey, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [state, setState] = useState("form"); // form | error | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("e")) setState("error");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) { setError("Password must be at least 10 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset/complete", { password });
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 400) setState("error");
      else setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-page">
      <div className="max-w-md w-full border border-border bg-surface rounded-md p-8">
        <div className="h-10 w-10 bg-primary rounded flex items-center justify-center">
          <ShieldCheck size={22} weight="fill" className="text-white" />
        </div>

        {state === "error" && (
          <>
            <div className="mt-5 text-amber-600"><WarningCircle size={32} weight="duotone" /></div>
            <h1 className="mt-3 t-section-title" data-testid="reset-error-title">Reset link unavailable</h1>
            <p className="mt-2 t-support text-muted-foreground">
              This reset link cannot be used. It may be invalid, expired or already used.
              You can request a new one from the sign-in screen.
            </p>
            <button onClick={() => navigate("/forgot")} className="mt-5 text-sm text-primary hover:underline">
              Request a new link
            </button>
          </>
        )}

        {state === "done" && (
          <>
            <div className="mt-5 text-primary"><CheckCircle size={32} weight="duotone" /></div>
            <h1 className="mt-3 t-section-title" data-testid="reset-done-title">Password changed</h1>
            <p className="mt-2 t-support text-muted-foreground">
              Your password has been updated and every signed-in device has been signed out for safety.
              Please sign in with your new password.
            </p>
            <button data-testid="reset-go-login" onClick={() => { window.location.href = "/login"; }}
              className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 t-support font-semibold rounded-md hover:bg-primary/90">
              Go to sign in
            </button>
          </>
        )}

        {state === "form" && (
          <form onSubmit={submit} data-testid="reset-form">
            <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Password reset</p>
            <h1 className="mt-2 font-display font-extrabold tracking-tighter text-2xl">Choose a new password</h1>
            <label className="block mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">New password</label>
            <input
              data-testid="reset-password-input" type="password" required minLength={10} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">Minimum 10 characters. Passphrases welcome.</p>
            <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Confirm password</label>
            <input
              data-testid="reset-confirm-input" type="password" required autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {error && <p data-testid="reset-error" className="mt-3 text-sm text-red-700">{error}</p>}
            <button
              data-testid="reset-submit-button" type="submit" disabled={submitting}
              className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {submitting ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <LockKey size={18} weight="bold" />}
              Change password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
